import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { MessageCircle, Loader2 } from 'lucide-react';
import { formatDate, getInitials } from '@/lib/utils';
import { messagesAPI } from '@/lib/api';

export const MessagesHoverMenu = ({ unreadMessages }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [conversations, setConversations] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const timeoutRef = useRef(null);
    const scrollContainerRef = useRef(null);
    const navigate = useNavigate();
    const PAGE_SIZE = 15;

    const loadConversations = useCallback(async (skip = 0, append = false) => {
        try {
            const res = await messagesAPI.getRecent(skip, PAGE_SIZE);
            const data = res.data || {};
            const newConversations = data.conversations || [];
            
            if (append) {
                setConversations(prev => [...prev, ...newConversations]);
            } else {
                setConversations(newConversations);
            }
            setHasMore(data.has_more || false);
        } catch (error) {
            console.error('Error fetching conversations:', error);
        }
    }, []);

    const handleMouseEnter = async () => {
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        setIsOpen(true);
        
        if (!loading && conversations.length === 0) {
            setLoading(true);
            await loadConversations(0, false);
            setLoading(false);
        }
    };

    const handleMouseLeave = () => {
        timeoutRef.current = setTimeout(() => setIsOpen(false), 100);
    };

    const handleToggle = () => {
        setIsOpen(true);
    };

    const handleMessageClick = (conv) => {
        navigate(`/messages/${conv.conversation_id}`);
        setIsOpen(false);
    };

    // Handle scroll for infinite loading
    const handleScroll = useCallback(async (e) => {
        const container = e.target;
        const { scrollTop, scrollHeight, clientHeight } = container;
        
        // Load more when near bottom (within 50px)
        if (scrollHeight - scrollTop - clientHeight < 50 && hasMore && !loadingMore) {
            setLoadingMore(true);
            await loadConversations(conversations.length, true);
            setLoadingMore(false);
        }
    }, [hasMore, loadingMore, conversations.length, loadConversations]);

    // Refresh when opened
    useEffect(() => {
        if (isOpen && conversations.length > 0) {
            loadConversations(0, false);
        }
    }, [isOpen]);

    return (
        <div 
            className="relative"
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
        >
            <Button 
                variant="ghost" 
                size="icon" 
                className="relative rounded-full"
                onClick={() => navigate('/messages')}
                onMouseEnter={handleMouseEnter}
            >
                <MessageCircle className="w-5 h-5" />
                {unreadMessages > 0 && (
                    <span className="absolute -top-1 -right-1 w-5 h-5 flex items-center justify-center bg-red-500 text-white text-xs rounded-full">
                        {unreadMessages > 9 ? '9+' : unreadMessages}
                    </span>
                )}
            </Button>
            
            {isOpen && (
                <div className="absolute top-full right-[-50px] mt-1 w-80 z-[100] rounded-xl border border-gray-200 shadow-lg overflow-hidden bg-white/95 backdrop-blur-sm">
                    {/* Content */}
                    <div>
                    <div className="flex items-center justify-between px-4 py-3 border-b bg-gray-50/80">
                        <h3 className="font-semibold">Mesaje</h3>
                        <Button 
                            variant="ghost" 
                            size="sm" 
                            className="text-xs h-7 px-2 text-primary"
                            onClick={() => { navigate('/messages'); setIsOpen(false); }}
                        >
                            Vezi toate
                        </Button>
                    </div>
                    
                    <div 
                        ref={scrollContainerRef}
                        className="max-h-[350px] overflow-y-auto"
                        onScroll={handleScroll}
                    >
                        {loading ? (
                            <div className="py-8 text-center text-muted-foreground">
                                <div className="animate-spin w-6 h-6 border-2 border-primary border-t-transparent rounded-full mx-auto mb-2" />
                                <p className="text-sm">Se încarcă...</p>
                            </div>
                        ) : conversations.length === 0 ? (
                            <div className="py-8 text-center text-muted-foreground">
                                <MessageCircle className="w-8 h-8 mx-auto mb-2 opacity-50" />
                                <p className="text-sm">Nu ai mesaje</p>
                            </div>
                        ) : (
                            <div className="p-1">
                                {conversations.map((conv) => (
                                    <button
                                        key={conv.conversation_id}
                                        onClick={() => handleMessageClick(conv)}
                                        className={`w-full px-3 py-3 bg-transparent hover:bg-gray-200 transition-colors text-left rounded-lg ${
                                            conv.is_unread ? 'bg-blue-50/50' : ''
                                        }`}
                                    >
                                        <div className="flex items-start gap-3">
                                            <Avatar className="w-10 h-10 shrink-0">
                                                <AvatarFallback className="bg-primary text-white text-sm">
                                                    {getInitials(conv.other_name || conv.other_username)}
                                                </AvatarFallback>
                                            </Avatar>
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center justify-between">
                                                    <p className={`font-medium text-sm truncate ${conv.is_unread ? 'text-foreground' : 'text-muted-foreground'}`}>
                                                        {conv.other_name || conv.other_username}
                                                    </p>
                                                    {conv.is_unread && (
                                                        <span className="w-2 h-2 bg-red-500 rounded-full shrink-0 ml-2" />
                                                    )}
                                                </div>
                                                {conv.listing_title && (
                                                    <p className="text-xs text-primary truncate">
                                                        {conv.listing_title}
                                                    </p>
                                                )}
                                                <p className={`text-xs truncate mt-0.5 ${conv.is_unread ? 'text-foreground font-medium' : 'text-muted-foreground'}`}>
                                                    {conv.is_from_me ? 'Tu: ' : ''}{conv.last_message}
                                                </p>
                                                <p className="text-xs text-muted-foreground mt-0.5">
                                                    {formatDate(conv.last_message_at)}
                                                </p>
                                            </div>
                                        </div>
                                    </button>
                                ))}
                                
                                {/* Load more indicator */}
                                {loadingMore && (
                                    <div className="flex justify-center py-3">
                                        <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
                                    </div>
                                )}
                                
                                {!hasMore && conversations.length > PAGE_SIZE && (
                                    <p className="text-center text-xs text-muted-foreground py-2">
                                        Toate conversațiile încărcate
                                    </p>
                                )}
                            </div>
                        )}
                    </div>
                    </div>
                </div>
            )}
        </div>
    );
};
